/**
 * Emulator Page with Desmond Core
 * Uses the flexible core architecture with Desmond as default
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { calculateROMHash } from '../utils/romUtils';
import { EmulatorCoreFactory, EmulatorCoreType, IEmulatorCore } from '../emulator/cores';
import { DesmondCore } from '../emulator/cores/desmond/DesmondCore';
import { DesmondKeyboardRemapper } from '../emulator/DesmondKeyboardRemapper';
import { loadKeyMapping } from '../emulator/InputManager';
import { cloudSaveService } from '../services/CloudSaveService';
import { computeGameID, readDesmondSave, writeDesmondSave } from '../services/DesmondSaveBridge';
import KeyBindingsSettings from '../components/KeyBindingsSettings';
import toast from 'react-hot-toast';
import '../styles/emulator.css';

export default function EmulatorDesmond() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const desmondContainerRef = useRef<HTMLDivElement>(null);

  // State
  const [emulatorCore, setEmulatorCore] = useState<IEmulatorCore | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [romInfo, setRomInfo] = useState<{ hash: string; name: string; size: number } | null>(null);
  const [showKeyBindings, setShowKeyBindings] = useState(false);
  const [cloudSyncEnabled, setCloudSyncEnabled] = useState(false);
  const remapperRef = useRef<DesmondKeyboardRemapper | null>(null);
  const gameIDRef = useRef<string | null>(null);
  const cloudSyncTimerRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Initialize emulator core
   */
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    const init = async () => {
      try {
        console.log('🔧 DEBUG: Setting isLoading = true');
        setIsLoading(true);

        // Safety timeout - force unlock after 20 seconds
        timeoutId = setTimeout(() => {
          console.warn('⚠️ FORCE UNLOCK: Initialization took too long, forcing isLoading = false');
          setIsLoading(false);
          setError('Initialization timed out. The emulator may not work properly. Try refreshing the page.');
        }, 20000);

        console.log('🎮 Initializing emulator with flexible core system...');

        // IMPORTANT: Create and add <desmond-player> to DOM BEFORE loading the script
        // The desmond.js script looks for this element when it loads
        if (desmondContainerRef.current) {
          const playerElement = document.createElement('desmond-player');
          playerElement.id = 'desmond-player-instance';
          playerElement.style.width = '100%';
          playerElement.style.height = 'auto';
          desmondContainerRef.current.appendChild(playerElement);
          console.log('✅ Created <desmond-player> element and added to DOM (before loading script)');
        }

        // Now create Desmond core (which will load the script)
        // The script will find the element we just created and initialize it
        const core = await EmulatorCoreFactory.createCoreWithFallback({
          preferredCore: EmulatorCoreType.DESMOND,
          fallbackOrder: [EmulatorCoreType.DESMOND],
          autoDetectBestCore: false,
        });

        setEmulatorCore(core);

        // Log core metadata
        const metadata = core.getMetadata();
        console.log(`✅ Using ${metadata.name} v${metadata.version}`);
        console.log(`📋 Description: ${metadata.description}`);
        console.log(`Features:`, metadata.features);
        if (metadata.limitations && metadata.limitations.length > 0) {
          console.warn(`⚠️ Limitations:`, metadata.limitations);
        }

        // Set the player element reference in the core
        if (core instanceof DesmondCore && desmondContainerRef.current) {
          const playerElement = desmondContainerRef.current.querySelector('desmond-player');
          if (playerElement) {
            core.setPlayerElement(playerElement as any);
            console.log('✅ Set player element reference in DesmondCore');
          }
        }

        // Install keyboard remapper after Desmond has set its handlers
        const remapper = new DesmondKeyboardRemapper();
        await remapper.install();
        remapperRef.current = remapper;
        console.log('✅ Keyboard remapper installed');

        clearTimeout(timeoutId);
        console.log('🔧 DEBUG: Setting isLoading = false (success)');
        setIsLoading(false);
      } catch (err) {
        clearTimeout(timeoutId);
        console.error('❌ Emulator initialization failed:', err);
        setError(err instanceof Error ? err.message : 'Failed to initialize emulator');
        setIsLoading(false);
      }
    };

    init();

    // Cleanup
    return () => {
      clearTimeout(timeoutId);
      if (cloudSyncTimerRef.current) {
        clearInterval(cloudSyncTimerRef.current);
        cloudSyncTimerRef.current = null;
      }
      if (remapperRef.current) {
        remapperRef.current.uninstall();
        remapperRef.current = null;
      }
      if (emulatorCore) {
        emulatorCore.destroy();
      }
    };
  }, []);

  /**
   * Handle ROM upload
   */
  const handleROMUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !emulatorCore) return;

    // Stop any existing cloud sync timer
    if (cloudSyncTimerRef.current) {
      clearInterval(cloudSyncTimerRef.current);
      cloudSyncTimerRef.current = null;
    }

    try {
      setIsLoading(true);
      setError(null);

      const fileSize = file.size;
      const fileSizeMB = (fileSize / 1024 / 1024).toFixed(2);

      console.log(`Loading ROM: ${file.name} (${fileSizeMB} MB)`);
      toast.loading('Loading ROM...', { id: 'rom-load' });

      const romBuffer = await file.arrayBuffer();
      const hash = await calculateROMHash(romBuffer);
      const gameID = computeGameID(romBuffer, file.name);
      gameIDRef.current = gameID;

      console.log(`ROM hash: ${hash.substring(0, 16)}... | gameID: ${gameID.trim()}`);

      // Before loading ROM: check cloud for an existing save and inject into localforage
      // so Desmond auto-loads it when the ROM starts
      let cloudSyncActive = false;
      try {
        const cloudSave = await cloudSaveService.getSaveByRomHash(hash);
        if (cloudSave) {
          const saveData = await cloudSaveService.downloadSave(cloudSave.id);
          await writeDesmondSave(gameID, saveData);
          console.log('Cloud save restored to localforage');
          toast.success('Cloud save restored!', { id: 'cloud-restore' });
        }
        cloudSyncActive = true;
      } catch (err) {
        // Cloud unavailable (not logged in, network error) — proceed without cloud
        console.warn('Cloud save check failed (continuing without cloud):', err);
      }

      // Load ROM into emulator — Desmond auto-loads save from localforage
      await emulatorCore.loadROM(romBuffer);

      const romName = file.name.replace(/\.(nds|ds)$/i, '');
      setRomInfo({ hash, name: romName, size: fileSize });
      setCloudSyncEnabled(cloudSyncActive);

      // Start periodic cloud sync (every 2 minutes)
      if (cloudSyncActive) {
        cloudSyncTimerRef.current = setInterval(async () => {
          try {
            const saveData = await readDesmondSave(gameID);
            if (saveData && saveData.length > 0) {
              await cloudSaveService.uploadSave(hash, saveData, romName);
              console.log('Cloud sync completed');
            }
          } catch (err) {
            console.warn('Cloud sync failed:', err);
          }
        }, 2 * 60 * 1000);
      }

      toast.dismiss('rom-load');
      toast.success('ROM loaded successfully!');
      setIsRunning(true);
    } catch (err) {
      console.error('ROM loading failed:', err);
      toast.dismiss('rom-load');
      toast.error(err instanceof Error ? err.message : 'Failed to load ROM');
      setError(err instanceof Error ? err.message : 'Failed to load ROM');
    } finally {
      setIsLoading(false);
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /**
   * Handle key bindings settings close — reload mapping into remapper
   */
  const handleKeyBindingsClose = () => {
    setShowKeyBindings(false);
    if (remapperRef.current) {
      remapperRef.current.rebuildLookup(loadKeyMapping());
    }
  };

  /**
   * Trigger a manual cloud save
   */
  const handleManualCloudSave = async () => {
    if (!gameIDRef.current || !romInfo) {
      toast.error('No ROM loaded');
      return;
    }
    try {
      toast.loading('Saving to cloud...', { id: 'manual-save' });
      const saveData = await readDesmondSave(gameIDRef.current);
      if (!saveData || saveData.length === 0) {
        toast.dismiss('manual-save');
        toast('No save data found yet. Play the game first!', { duration: 3000 });
        return;
      }
      await cloudSaveService.uploadSave(romInfo.hash, saveData, romInfo.name);
      toast.dismiss('manual-save');
      toast.success('Saved to cloud!');
    } catch (err) {
      toast.dismiss('manual-save');
      toast.error('Cloud save failed. Are you logged in?');
      console.warn('Manual cloud save failed:', err);
    }
  };

  /**
   * Save to cloud before navigating away
   */
  const finalCloudSync = async () => {
    if (!cloudSyncEnabled || !gameIDRef.current || !romInfo) return;
    try {
      const saveData = await readDesmondSave(gameIDRef.current);
      if (saveData && saveData.length > 0) {
        await cloudSaveService.uploadSave(romInfo.hash, saveData, romInfo.name);
        console.log('Final cloud sync completed');
      }
    } catch (err) {
      console.warn('Final cloud sync failed:', err);
    }
  };

  /**
   * Go back to dashboard
   */
  const handleBackToDashboard = async () => {
    if (isRunning) {
      if (window.confirm('Exit the emulator? Your progress will be saved.')) {
        await finalCloudSync();
        navigate('/dashboard');
      }
    } else {
      navigate('/dashboard');
    }
  };

  // Get core metadata for display
  const coreMetadata = emulatorCore?.getMetadata();

  return (
    <div className="emulator-page">
      <div className="emulator-container">
        <div className="emulator-header">
          <h1>🎮 Nintendo DS Emulator</h1>
          <button
            onClick={handleBackToDashboard}
            className="back-button"
            aria-label="Return to dashboard"
          >
            ← Back to Dashboard
          </button>
        </div>

        {error && (
          <div className="error-message">
            <strong>Error:</strong> {error}
            <button onClick={() => setError(null)} aria-label="Close error message">
              ✕
            </button>
          </div>
        )}

        {/* Core Info Banner */}
        {coreMetadata && (
          <div className="core-info-banner">
            <strong>{coreMetadata.name} v{coreMetadata.version}</strong>
            {coreMetadata.limitations && coreMetadata.limitations.length > 0 && (
              <div className="core-limitations">
                {coreMetadata.limitations.map((limitation, idx) => (
                  <div key={idx} className="limitation-item">
                    ⚠️ {limitation}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="emulator-content">
          {/* Desmond player container */}
          <div ref={desmondContainerRef} className="desmond-container" />

          {/* ROM Info */}
          {romInfo && (
            <div className="rom-info-panel">
              <h3>📂 Current Game</h3>
              <div className="rom-details">
                <p>
                  <strong>Name:</strong> {romInfo.name}
                </p>
                <p>
                  <strong>Size:</strong> {(romInfo.size / 1024 / 1024).toFixed(2)} MB
                </p>
                <p>
                  <strong>Hash:</strong> {romInfo.hash.substring(0, 16)}...
                </p>
              </div>
            </div>
          )}

          {/* Controls Panel */}
          <div className="controls-panel">
            {!isRunning && (
              <div className="control-group">
                <label htmlFor="rom-upload" className="upload-button">
                  {isLoading && !emulatorCore && '⏳ Initializing Emulator...'}
                  {isLoading && emulatorCore && '⏳ Loading ROM...'}
                  {!isLoading && '📂 Upload ROM File (.nds)'}
                </label>
                <input
                  ref={fileInputRef}
                  id="rom-upload"
                  type="file"
                  accept=".nds,.ds"
                  onChange={handleROMUpload}
                  disabled={isLoading}
                  aria-label="Upload Nintendo DS ROM file"
                  style={{ display: 'none' }}
                />
                {isLoading && !emulatorCore && (
                  <p style={{ fontSize: '0.9rem', color: '#ff6b6b', marginTop: '10px', textAlign: 'center' }}>
                    ⏱️ If this takes more than 10 seconds, please refresh the page
                  </p>
                )}
              </div>
            )}

            {isRunning && (
              <>
                <div className="control-buttons">
                  <button
                    onClick={() => setShowKeyBindings(true)}
                    className="control-button"
                    aria-label="Customize keyboard controls"
                  >
                    Keyboard Settings
                  </button>

                  <button
                    onClick={handleManualCloudSave}
                    className="control-button"
                    aria-label="Save to cloud"
                  >
                    Save to Cloud
                  </button>

                  <label htmlFor="rom-change" className="control-button">
                    📂 Load Different ROM
                  </label>
                  <input
                    id="rom-change"
                    type="file"
                    accept=".nds,.ds"
                    onChange={handleROMUpload}
                    disabled={isLoading}
                    aria-label="Load a different ROM file"
                    style={{ display: 'none' }}
                  />
                </div>

                <div className="status-info">
                  <div className="auto-save-status">
                    {cloudSyncEnabled
                      ? 'Auto-save: local + cloud sync every 2 min'
                      : 'Auto-save: local browser storage only'}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Key Bindings Settings Modal */}
        <KeyBindingsSettings
          isOpen={showKeyBindings}
          onClose={handleKeyBindingsClose}
        />

        {/* Keyboard Guide */}
        <div className="keyboard-guide">
          <h3>Keyboard Controls</h3>
          <p className="keyboard-note">
            Default controls shown below. Click <strong>Keyboard Settings</strong> above to customize.
          </p>
          <div className="key-mappings">
            <div className="key-group">
              <strong>Movement:</strong>
              <span>Arrow Keys: D-Pad</span>
            </div>
            <div className="key-group">
              <strong>Buttons:</strong>
              <span>Z: A</span>
              <span>X: B</span>
              <span>A: Y</span>
              <span>S: X</span>
            </div>
            <div className="key-group">
              <strong>Shoulders:</strong>
              <span>Q: L</span>
              <span>W: R</span>
            </div>
            <div className="key-group">
              <strong>System:</strong>
              <span>Enter: Start</span>
              <span>Shift: Select</span>
            </div>
          </div>
          <p className="keyboard-note">
            Touch Screen: Click on the bottom screen to use touch controls.
          </p>
        </div>

        {/* Legal Notice */}
        <div className="legal-notice">
          <h4>⚠️ Legal Notice</h4>
          <p>
            You must legally own the games you play. This emulator is for personal use only. ROMs
            of games you do not own are illegal.
          </p>
          <p>
            <strong>Free Homebrew Games:</strong>{' '}
            <a
              href="https://www.gamebrew.org/wiki/Category:Nintendo_DS_homebrew_games"
              target="_blank"
              rel="noopener noreferrer"
            >
              GameBrew.org
            </a>
          </p>
        </div>

        {/* Technical Info */}
        <div className="tech-info">
          <p>
            <strong>Emulator Core:</strong> {coreMetadata?.name || 'Loading...'}
          </p>
          <p>
            <strong>Architecture:</strong> Flexible Core System
          </p>
          <p>
            <strong>Status:</strong>{' '}
            {isLoading && '⏳ Loading...'}
            {!isLoading && !isRunning && '✓ Ready'}
            {isRunning && '▶️ Running'}
          </p>
        </div>
      </div>
    </div>
  );
}
